/**
 * CreateInvoiceDialog Component
 *
 * Dialog for creating new client invoices.
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Loader2,
  CalendarIcon,
  DollarSign,
  Package,
  User,
  AlertTriangle,
  ArrowRight,
} from "lucide-react";
import {
  useInvoices,
  InvoiceType,
  INVOICE_TYPE_CONFIG,
  CreateInvoiceParams,
} from "@/hooks/use-invoices";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/use-organization";
import { useStripeConnect } from "@/hooks/use-stripe-connect";
import { format } from "date-fns";
import { cn } from "@/lib/utils";

interface Client {
  id: string;
  name: string;
  email: string | null;
}

interface CreateInvoiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  preselectedClientId?: string;
}

export function CreateInvoiceDialog({
  open,
  onOpenChange,
  onSuccess,
  preselectedClientId,
}: CreateInvoiceDialogProps) {
  const navigate = useNavigate();
  const { organization } = useOrganization();
  const { createInvoice } = useInvoices();
  const {
    isConnected: stripeConnected,
    isPending: stripePending,
    loading: stripeLoading,
  } = useStripeConnect();
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);

  // Form state
  const [clientId, setClientId] = useState(preselectedClientId || "");
  const [invoiceType, setInvoiceType] = useState<InvoiceType>("package");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [packageSize, setPackageSize] = useState("");
  const [dueDate, setDueDate] = useState<Date>();
  const [sendEmail, setSendEmail] = useState(true);
  const [notes, setNotes] = useState("");

  // Load clients on mount
  useEffect(() => {
    if (open) {
      loadClients();
      if (preselectedClientId) {
        setClientId(preselectedClientId);
      }
    }
  }, [open, preselectedClientId]);

  const loadClients = async () => {
    setLoadingClients(true);
    try {
      if (!organization?.id) return;

      const { data, error } = await supabase
        .from("clients")
        .select("id, name, email")
        .eq("organization_id", organization.id)
        .eq("status", "active")
        .order("name");

      if (error) throw error;
      setClients(data || []);
    } catch (error) {
      console.error("Error loading clients:", error);
    } finally {
      setLoadingClients(false);
    }
  };

  const resetForm = () => {
    setClientId(preselectedClientId || "");
    setInvoiceType("package");
    setDescription("");
    setAmount("");
    setPackageSize("");
    setDueDate(undefined);
    setSendEmail(true);
    setNotes("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!clientId || !amount) return;
    // Client-side short-circuit. Server enforces the same check in
    // create-client-invoice (returns 400 if charges_enabled is false).
    if (!stripeConnected) return;

    setLoading(true);
    try {
      const amountCents = Math.round(parseFloat(amount) * 100);

      const params: CreateInvoiceParams = {
        client_id: clientId,
        invoice_type: invoiceType,
        description: description || undefined,
        subtotal_cents: amountCents,
        due_date: dueDate?.toISOString().split("T")[0],
        package_size: packageSize ? parseInt(packageSize) : undefined,
        send_email: sendEmail,
        notes: notes || undefined,
      };

      await createInvoice(params);
      resetForm();
      onSuccess();
    } catch (error) {
      // Error is handled in the hook
    } finally {
      setLoading(false);
    }
  };

  const selectedClient = clients.find((c) => c.id === clientId);

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        if (!value) resetForm();
        onOpenChange(value);
      }}
    >
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Invoice</DialogTitle>
          <DialogDescription>
            Create a new invoice with a payment link for your client.
          </DialogDescription>
        </DialogHeader>

        {/* Stripe Connect status gate — source of truth is server-side, but we
            surface the inline banner + disable the submit so trainers aren't
            surprised by a server rejection after filling out the form. */}
        {!stripeLoading && !stripeConnected && (
          <div
            role="alert"
            className="flex items-start gap-3 rounded-md border border-warning/40 bg-warning/10 p-3"
          >
            <AlertTriangle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
            <div className="flex-1 text-sm">
              <p className="font-medium text-foreground">
                {stripePending
                  ? "Stripe Connect setup is not complete."
                  : "Stripe Connect is not set up."}
              </p>
              <p className="text-muted-foreground text-xs mt-0.5">
                Finish onboarding before sending invoices — clients can't be charged until
                your account can accept payments.
              </p>
              <Button
                type="button"
                variant="link"
                size="sm"
                className="h-auto p-0 mt-1 text-sm text-primary"
                onClick={() => {
                  onOpenChange(false);
                  navigate("/settings?section=billing");
                }}
              >
                Complete setup
                <ArrowRight className="h-3 w-3 ml-1" />
              </Button>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Client Selection */}
          <div className="space-y-2">
            <Label htmlFor="client">Client *</Label>
            <Select value={clientId} onValueChange={setClientId} disabled={!!preselectedClientId}>
              <SelectTrigger>
                <SelectValue placeholder={loadingClients ? "Loading..." : "Select a client"} />
              </SelectTrigger>
              <SelectContent>
                {clients.map((client) => (
                  <SelectItem key={client.id} value={client.id}>
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      {client.name}
                      {client.email && (
                        <span className="text-muted-foreground text-xs">
                          ({client.email})
                        </span>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedClient && !selectedClient.email && (
              <p className="text-xs text-warning">
                This client has no email address. You can still create the invoice and share the payment link manually.
              </p>
            )}
          </div>

          {/* Invoice Type */}
          <div className="space-y-2">
            <Label htmlFor="type">Invoice Type</Label>
            <Select value={invoiceType} onValueChange={(v) => setInvoiceType(v as InvoiceType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(INVOICE_TYPE_CONFIG).map(([value, config]) => (
                  <SelectItem key={value} value={value}>
                    <div>
                      <span>{config.label}</span>
                      <span className="text-muted-foreground text-xs ml-2">
                        {config.description}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Package Size (for package invoices) */}
          {invoiceType === "package" && (
            <div className="space-y-2">
              <Label htmlFor="packageSize">Package Size (sessions)</Label>
              <div className="relative">
                <Package className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="packageSize"
                  type="number"
                  min="1"
                  placeholder="e.g., 10"
                  value={packageSize}
                  onChange={(e) => setPackageSize(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
          )}

          {/* Amount */}
          <div className="space-y-2">
            <Label htmlFor="amount">Amount *</Label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="amount"
                type="number"
                min="0.01"
                step="0.01"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="pl-9"
                required
              />
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Input
              id="description"
              placeholder={
                invoiceType === "package"
                  ? `Personal Training Package${packageSize ? ` (${packageSize} sessions)` : ""}`
                  : "Brief description of the invoice"
              }
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          {/* Due Date */}
          <div className="space-y-2">
            <Label>Due Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !dueDate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dueDate ? format(dueDate, "PPP") : "Select a due date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={dueDate}
                  onSelect={setDueDate}
                  disabled={(date) => date < new Date()}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Internal Notes</Label>
            <Textarea
              id="notes"
              placeholder="Notes for your reference (not visible to client)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
            />
          </div>

          {/* Send Email Toggle */}
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label htmlFor="sendEmail">Send email to client</Label>
              <p className="text-xs text-muted-foreground">
                {selectedClient?.email
                  ? `Email will be sent to ${selectedClient.email}`
                  : "Client has no email address"}
              </p>
            </div>
            <Switch
              id="sendEmail"
              checked={sendEmail && !!selectedClient?.email}
              onCheckedChange={setSendEmail}
              disabled={!selectedClient?.email}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || !clientId || !amount || !stripeConnected || stripeLoading}
              title={
                !stripeConnected && !stripeLoading
                  ? "Complete Stripe Connect setup before creating invoices"
                  : undefined
              }
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                "Create Invoice"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
